import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { GoonlordsWordmark } from "../../ui/Logo";

/**
 * The shell every legal page wears: mark, title, revision date, body, back link.
 *
 * One shell rather than one per page, because these pages exist to be checked — by a
 * user, an ad network, a host, a rights holder — and five hand-built copies of the same
 * chrome is five chances for one of them to look like a different site than the others.
 *
 * Kept deliberately plain. Nothing here should need JavaScript to be readable, and
 * nothing here should be the interesting part of the page.
 */
export function LegalPage({
  title,
  updated,
  children,
}: {
  title: string;
  /** Shown as "Last updated". Bump it whenever the text below changes materially. */
  updated: string;
  children: ReactNode;
}) {
  return (
    <div className="mx-auto max-w-3xl px-6 py-12">
      <div className="mb-8 flex items-center gap-2">
        <GoonlordsWordmark height={28} />
        <h1 className="text-2xl font-semibold tracking-tight text-zinc-100">{title}</h1>
      </div>

      <p className="mb-8 text-sm text-zinc-500">Last updated: {updated}</p>

      <div className="space-y-6 text-sm leading-relaxed text-zinc-300">{children}</div>

      <div className="mt-10 border-t border-[var(--color-edge)] pt-6">
        <Link to="/" className="text-sm text-[var(--color-accent)] hover:underline">
          ← Back to goonlords
        </Link>
      </div>
    </div>
  );
}

export function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h2 className="mb-2 text-base font-semibold text-zinc-100">{title}</h2>
      <div className="text-zinc-400">{children}</div>
    </section>
  );
}

/**
 * How the operator is named, in one place.
 *
 * The SERVICE's name, not a company's — there is no incorporated entity, and asserting
 * one that does not exist is a misrepresentation sitting in the middle of the pages whose
 * only job is to be accurate. These pages therefore claim nothing about corporate form.
 *
 * Where a real legal name is required by statute — the DMCA designated agent and the
 * §2257 custodian of records — it is a placeholder to be filled with a natural person's
 * name, because without an entity that is who the operator legally is.
 */
export const OPERATOR = "goonlords";
export const CONTACT_EMAIL = "admin@goonlords.com";

export function MailTo({ subject }: { subject?: string }) {
  const href = subject
    ? `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}`
    : `mailto:${CONTACT_EMAIL}`;
  return (
    <a href={href} className="text-[var(--color-accent)] hover:underline">
      {CONTACT_EMAIL}
    </a>
  );
}
